import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import React, { useMemo, useState } from "react";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import FullTaskSplit from "../../Layouts/FullTask/FullTaskSplit";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TitleType } from "../../Core/Api/Types/title-type";
import { assetService } from "../../Core/Api/services";
import { useSelector } from "react-redux";
import {
  GetBroadcasterList,
  GetCulturesList,
  GetMetadata,
  GetPackageList,
  GetSelectedPackageIndex,
  GetSelectedTitle,
  GetStatusMap,
  HandlePackageChange,
  RemovePackage,
  SetEditTitle,
  SetMetadataMap,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import { useDispatch } from "react-redux";
import {
  getAssetCultures,
  getBroacasterIdMetadata,
  getBroadcasterImageSrc,
  getBroadcasterName,
  getEpisodeMetadata,
  getTitleType,
} from "../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { ModalContext } from "../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import WarningIcon from "@mui/icons-material/Warning";
import { useTranslation } from "react-i18next";

const COUNTRY_TO_SHOW_COUNT = 3;

interface LeftPanelBundlemakerProps {
  isLoading: boolean;
  isSaving: boolean;
  openCreatePackageModal: () => void;
}

const LeftPanelBundleMaker: React.FC<LeftPanelBundlemakerProps> = (props: LeftPanelBundlemakerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  //------Select store values------

  const packageList = useSelector(GetPackageList);
  const broadcasters = useSelector(GetBroadcasterList);
  const titleType = getTitleType(useSelector(GetSelectedTitle));

  const { isLoading, isSaving, openCreatePackageModal } = props;

  // Helpers function

  const generatePackageList = () => {
    if (!packageList) return <></>;
    let packageMap: any = {};

    // Group packages by broadcaster in packageMap
    for (let i = 0; i < packageList.length; i++) {
      const broadcasterId = getBroacasterIdMetadata(packageList[i]);
      if (broadcasterId) {
        if (packageMap[broadcasterId] != null && packageMap[broadcasterId] != undefined) {
          packageMap[broadcasterId].push(packageList[i]);
        } else {
          packageMap[broadcasterId] = [packageList[i]];
        }
      }
    }

    // track package count to set index value

    let count = -1;

    const generated = Object.keys(packageMap).map((x, i) => {
      count += 1;
      if (i > 0) count += Object.keys(packageMap)[i - 1].length;
      return (
        <PackageItemGroup
          isLoading={isLoading}
          isSaving={isSaving}
          titleType={titleType}
          data={packageMap[x]}
          index={count}
          key={count}
          imageSrc={getBroadcasterImageSrc(getBroacasterIdMetadata(packageMap[x][0]), broadcasters)}
          broadcasterName={getBroadcasterName(getBroacasterIdMetadata(packageMap[x][0]), broadcasters)}
          packageList={packageList}
        ></PackageItemGroup>
      );
    });

    return <>{generated}</>;
  };

  return (
    <FullTaskSplit
      layout="1:6"
      sxContainer={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "0px",
      }}
      sx={{ marginTop: "85px", minWidth: "320px" }}
    >
      <Stack
        sx={{
          overflowY: "scroll",
          width: "100%",
          ...theme.helpers(theme).scrollableContainer,
        }}
        height="70vh"
        position="relative"
      >
        <List>{packageList && packageList.length > 0 && generatePackageList()}</List>
      </Stack>
      <Stack
        width={"-webkit-fill-available"}
        alignItems="center"
        justifyContent="center"
        position="absolute"
        bottom={"12px"}
        height={"10vh"}
        zIndex={1}
      >
        <Button
          sx={{ width: "85%" }}
          variant="contained"
          color="primary"
          disabled={isLoading || isSaving}
          onClick={openCreatePackageModal}
        >
          {t("button:createPackage")}
        </Button>
      </Stack>
    </FullTaskSplit>
  );
};

// Single package item component
const PackageConfig: React.FC<{
  data: DbAssetObjectInfos;
  title: string;
  index: number;
  selectedPackageIndex: number;
  isLast: boolean;
  isDeleting: number;
  isLoading: boolean;
  isSaving: boolean;
  handleDeletePackage: (packageIds: number[], keys: number[]) => void;
}> = (props) => {
  const cultures = useSelector(GetCulturesList);
  const dispatch = useDispatch();
  const meta = useSelector(GetMetadata);
  const packageList = useSelector(GetPackageList);
  const statusMap = useSelector(GetStatusMap);

  const theme = useTheme();

  const handlePackageChange = (data: DbAssetObjectInfos, key: number) => {
    dispatch(SetEditTitle(false));
    selectedPackageIndex >= 0 &&
      dispatch(SetMetadataMap({ packageId: packageList[selectedPackageIndex].id, metadata: meta }));
    dispatch(HandlePackageChange({ foundCultures: getAssetCultures(data, cultures), packageIndex: key }));
  };

  const { selectedPackageIndex, index, data, title, isLast, handleDeletePackage, isDeleting, isLoading, isSaving } =
    props;

  const status = useMemo(() => statusMap[data.id], [data, data.id, statusMap, packageList.length]);

  return (
    <>
      <Stack
        sx={{
          width: "100%",
        }}
        spacing={0.5}
        mb={isLast ? 0 : 2}
      >
        <StyledPackageContent selected={selectedPackageIndex === index}>
          {status && <WarningIcon sx={{ marginRight: "8px" }} color="warning" fontSize="small" />}
          <Tooltip placement="bottom" title={title}>
            <Typography sx={{ width: "70%" }}>
              {`${title.split(",").slice(0, COUNTRY_TO_SHOW_COUNT).join(", ")} ${
                title.split(",").length > COUNTRY_TO_SHOW_COUNT
                  ? `... + ${title.split(",").length - COUNTRY_TO_SHOW_COUNT}`
                  : ""
              }`}
            </Typography>
          </Tooltip>
          <Button
            variant="icon"
            size="small"
            onClick={() => handlePackageChange(data, index)}
            disabled={selectedPackageIndex === index || isLoading || isSaving}
          >
            <ModeEditOutlinedIcon />
          </Button>
          <Box sx={{ marginLeft: "6px", marginRight: "4px", position: "relative" }}>
            <Button
              disabled={isDeleting === index || isLoading || isSaving}
              variant="icon"
              size="small"
              onClick={() => handleDeletePackage([data.id], [index])}
            >
              <DeleteOutlinedIcon color="error" />
            </Button>
            {isDeleting === index && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </StyledPackageContent>
      </Stack>
    </>
  );
};

const StyledPackageItem = styled("div", {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: Boolean; selected: Boolean }>(({ theme, expanded, selected }) => ({
  borderRadius: 1,
  boxSizing: "border-box",
  border: selected ? `5px solid ${theme.palette.info}` : `1px solid ${theme.palette.backgroundAlpha.border}`,
  padding: theme.spacing(1.5),
  marginBottom: "15px",
  position: "relative",
  cursor: "",
  backgroundColor: selected && theme.palette.backgroundAlpha.borderFocus,
  transition: theme.transitions.create("backgroundColor", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "&:hover": {
    backgroundColor: theme.palette.backgroundAlpha.hover,
  },
  img: {
    borderRadius: "2px",
  },
  ...(expanded === true && {
    backgroundColor: theme.palette.backgroundAlpha.pressed,
    "&:hover": {
      backgroundColor: theme.palette.backgroundAlpha.pressed,
    },
  }),
}));

const StyledPackageContent = styled(Stack, { shouldForwardProp: (prop) => prop !== "selected" })<{ selected: Boolean }>(
  ({ theme, selected }) => ({
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
    borderRadius: 4,
    backgroundColor: selected && theme.palette.background[300],
    border: selected ? `1px solid #2D6CE1` : `1px solid ${theme.palette.backgroundAlpha.border}`,
    transition: theme.transitions.create("background", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    "&:hover": {
      backgroundColor: theme.palette.backgroundAlpha.hover,
      ".MuiButton-root": {
        opacity: 1,
      },
    },
    ".MuiButton-root": {
      opacity: 0,
      transition: theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  })
);

interface PackageGroupProps {
  data: DbAssetObjectInfos[];
  index: number;
  imageSrc: string;
  broadcasterName: string;
  titleType: TitleType;
  packageList: DbAssetObjectInfos[];
  isLoading: boolean;
  isSaving: boolean;
}

// Packages group by broadcaster
const PackageItemGroup: React.FC<PackageGroupProps> = (props) => {
  const { t } = useTranslation();
  const { data, index, imageSrc, broadcasterName, titleType, packageList, isLoading, isSaving } = props;

  const dispatch = useDispatch();
  const selectedPackageIndex = useSelector(GetSelectedPackageIndex);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const [isDeleting, setIsDeleting] = useState(-1);
  const [expanded, setExpanded] = React.useState(false);

  const isExpandable = data.length > 1;
  // Modals
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleDeletePackage = (packageIds: number[], keys: number[]) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.confirmDeleteBundlePackage"),
      onConfirm: () => {
        for (let i = 0; i < packageIds.length; i++) {
          setIsDeleting(keys[i]);

          assetService.DeleteObjectById(packageIds[i]).then((res) => {
            if (res.success) {
              dispatch(RemovePackage(packageIds[i]));
              enqueueCustomSnackbar(t("alert:success.packageDeleted"), "success");
            } else {
              enqueueCustomSnackbar(res.message || t("alert:error.deletingPackage"), "error");
            }
            setIsDeleting(-1);
          });
        }
      },
    });
  };

  return (
    <StyledPackageItem selected={selectedPackageIndex === index} expanded={expanded}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <img src={imageSrc} alt="" width={32} height={32} loading="lazy" />
        <Stack>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            mb={0.5}
            sx={(theme) => ({ ...theme.helpers(theme).clampedText })}
          >
            {broadcasterName.split(" ")[1] || broadcasterName}
          </Typography>
        </Stack>
        {isExpandable && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <MenuItem sx={{ color: "error.main", padding: 0, borderRadius: "100%" }}>
              <Box sx={{ position: "relative" }}>
                <IconButton
                  disabled={isDeleting === index || isLoading || isSaving}
                  onClick={() => {
                    handleDeletePackage(
                      data.map((x) => x.id),
                      data.map((x, i) => index + i)
                    );
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
                </IconButton>
                {isDeleting === index && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
            </MenuItem>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <KeyboardArrowDownIcon className="arrow-indicator" />
              ) : (
                <KeyboardArrowRightIcon className="arrow-indicator" />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>
      {isExpandable ? (
        <Collapse in={expanded}>
          <Box sx={{ width: "100%" }} mt={2}>
            {titleType === TitleType.Movie
              ? data.map((item, i) => (
                  <PackageConfig
                    selectedPackageIndex={selectedPackageIndex}
                    data={item}
                    title={item.name}
                    index={index + i}
                    key={index + i}
                    isLast={data.length - 1 === i}
                    handleDeletePackage={handleDeletePackage}
                    isDeleting={isDeleting}
                    isLoading={isLoading}
                    isSaving={isSaving}
                  />
                ))
              : data
                  .sort((a, b) => getEpisodeMetadata(a) - getEpisodeMetadata(b))
                  .map((x, i) => {
                    const episodeMetadata = getEpisodeMetadata(x);
                    let shouldShowEpisode = true;
                    const packageIndex = packageList.findIndex((p) => p.id === x.id);
                    if (
                      i > 0 &&
                      episodeMetadata ===
                        getEpisodeMetadata(data.sort((a, b) => getEpisodeMetadata(a) - getEpisodeMetadata(b))[i - 1])
                    ) {
                      shouldShowEpisode = false;
                    }
                    return (
                      <Box key={i}>
                        {shouldShowEpisode && (
                          <Typography sx={{ marginBottom: "10px" }} variant="h6">
                            {t("common:episode")} {episodeMetadata}
                          </Typography>
                        )}
                        <PackageConfig
                          selectedPackageIndex={selectedPackageIndex}
                          data={x}
                          title={x.name}
                          index={packageIndex}
                          key={packageIndex}
                          isLast={data.length - 1 === i}
                          handleDeletePackage={handleDeletePackage}
                          isDeleting={isDeleting}
                          isLoading={isLoading}
                          isSaving={isSaving}
                        />
                      </Box>
                    );
                  })}
          </Box>
        </Collapse>
      ) : (
        <Box mt={2}>
          <PackageConfig
            selectedPackageIndex={selectedPackageIndex}
            data={data[0] as DbAssetObjectInfos}
            title={data[0].name}
            index={index}
            isLast={true}
            handleDeletePackage={handleDeletePackage}
            isDeleting={isDeleting}
            isLoading={isLoading}
            isSaving={isSaving}
          />
        </Box>
      )}
    </StyledPackageItem>
  );
};

export default LeftPanelBundleMaker;
